/**
 * WorkshopPickerListRow — one selectable row in the picker's grouped-by-month
 * list view (design 02 mobile). Type + venue, the venue-local date and time, the
 * seats hint (or the reason it cannot be picked), and the price per seat.
 */
import type { PickerWorkshop } from './pickerWorkshop';
import {
  formatPickerDate,
  formatPickerTime,
  formatPricePerSeat,
  pickerAvailability,
  rowSeatsLabel,
} from './WorkshopPickerHelpers';
import { WorkshopTypeTile } from './WorkshopTypeTile';

export interface WorkshopPickerListRowProps {
  workshop: PickerWorkshop;
  selected: boolean;
  onSelect: (id: string) => void;
}

export function WorkshopPickerListRow({
  workshop,
  selected,
  onSelect,
}: WorkshopPickerListRowProps) {
  const { disabled } = pickerAvailability(workshop);
  const seatsLabel = rowSeatsLabel(workshop);
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      disabled={disabled}
      onClick={() => onSelect(workshop.id)}
      className={`w-full flex items-center gap-3 rounded-xl border p-3 text-left transition ${
        disabled
          ? 'cursor-not-allowed border-stone-200 opacity-50 dark:border-stone-700'
          : selected
            ? 'border-moss-600 bg-moss-50/60 dark:bg-moss-900/20'
            : 'border-stone-200 hover:border-stone-300 dark:border-stone-700'
      }`}
    >
      <WorkshopTypeTile name={workshop.treeSpeciesName} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-bark-900 dark:text-stone-100">
          {workshop.typeName} &middot; {workshop.venueName}
        </div>
        <div className="truncate text-xs text-stone-500 dark:text-stone-400">
          {formatPickerDate(workshop.localStartsAt)} &middot;{' '}
          {formatPickerTime(workshop.localStartsAt)}
          {' · '}
          {seatsLabel}
        </div>
      </div>
      <div className="flex-shrink-0 text-sm font-semibold text-bark-900 dark:text-stone-100">
        {formatPricePerSeat(workshop.priceCents)}
      </div>
    </button>
  );
}
